import {
  queryKeys,
  useHttpClient,
  useActiveOrganizationId,
} from '@repo/api-client/react';
import {
  SendInvoiceResponseSchema,
  type SendInvoiceResponse,
} from '@repo/api-client/dtos';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postJSON } from '../internal/http';

export { SendInvoiceResponseSchema, type SendInvoiceResponse } from '@repo/api-client/dtos';

export interface SendInvoiceVariables {
  invoiceId: string;
  /** Free-form note from the sender. Lands in the email body. */
  message?: string;
  /**
   * Override the client's on-file email address. When omitted the
   * server reads from `client.contact.email` and rejects the request
   * with a `ValidationError` if neither is present.
   */
  recipient?: string;
}

/**
 * Polling cadence after a successful send. The durable send pipeline
 * (`InvoicesService.sendEmail` → `IssuedInvoiceDocumentsService.startSend`)
 * returns synchronously with the invoice in `'pending'` status while a
 * BullMQ worker renders the PDF and hands it to the mail provider in
 * the background. The worker normally finishes inside 2–10s. Without
 * these follow-up invalidations the list and detail caches stay on
 * `'pending'` (which the mobile adapter renders as "Sending") until
 * something else triggers a refetch — empirically that meant "stays
 * Sending until app restart" for users who didn't navigate away.
 *
 * Geometric backoff hits the common case fast (most sends are flushed
 * by the second tick) without hammering the API for slow renders. We
 * stop polling unconditionally after the last tick — if the worker
 * still hasn't finished by then it's almost certainly a render or
 * provider failure, which the activity feed will surface separately.
 */
const SEND_POLL_DELAYS_MS = [2_000, 5_000, 10_000, 20_000] as const;

/**
 * Drive the "Send invoice" action.
 *
 * Different from `useTransitionInvoice({ to: 'sent' })`: that hook only
 * flips status (which is still useful for an automated workflow that
 * marks the invoice sent without actually emailing). `useSendInvoice`
 * is the user-facing "tap → email arrives in client's inbox" flow.
 *
 * On success we (1) write the freshly-returned invoice into the detail
 * cache (status flips from `draft` to `pending`/`sent` plus the now-
 * stamped `sentAt` / `publicShareToken`), (2) invalidate the list and
 * activity feed, and (3) schedule a short string of follow-up
 * invalidations so the durable pipeline's `pending → sent` transition
 * lands without the user having to swipe to refresh. Errors propagate
 * untouched so the calling screen can surface the server's actual
 * reason — `ValidationError` for missing client email, `ConflictError`
 * for already-paid invoices, upstream SMTP failures as 502 — through
 * the standard `NoticeSheet` pattern.
 */
export function useSendInvoice() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const orgId = useActiveOrganizationId();

  const mutation = useMutation({
    mutationFn: ({ invoiceId, message, recipient }: SendInvoiceVariables) => {
      if (!orgId) throw new Error('No active organization');
      return postJSON(
        http,
        `/orgs/${orgId}/invoices/${invoiceId}/send`,
        {
          ...(message ? { message } : {}),
          ...(recipient ? { recipient } : {}),
        },
        SendInvoiceResponseSchema,
      );
    },
    onSuccess: (response) => {
      const { invoice } = response;
      queryClient.setQueryData(
        queryKeys.invoices.detail(invoice.orgId, invoice.id),
        invoice,
      );
      queryClient.invalidateQueries({
        queryKey: queryKeys.invoices.all(invoice.orgId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.invoices.activities(invoice.orgId, invoice.id),
      });

      // Tail off in the background so `pending → sent` shows up
      // without a manual refresh. Each tick is a no-op if the cache
      // already reflects a terminal status — React Query just refetches
      // active observers and the latest server snapshot wins.
      for (const delay of SEND_POLL_DELAYS_MS) {
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: queryKeys.invoices.detail(invoice.orgId, invoice.id),
          });
          queryClient.invalidateQueries({
            queryKey: queryKeys.invoices.all(invoice.orgId),
          });
          queryClient.invalidateQueries({
            queryKey: queryKeys.invoices.activities(invoice.orgId, invoice.id),
          });
        }, delay);
      }
    },
  });

  return {
    sendInvoice: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error,
  };
}
